import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useAuth } from "auth";
import card_services from "services/card_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import type { CardRecord } from "components/card-row/CardRow";
import type { SpecificCard } from "utils/AxiosUtil";

/** Map the on-chain lookup shape onto the row model that CardRow renders. */
function toCardRecord(card: SpecificCard): CardRecord {
  return {
    cardNumber: card.cardNumber,
    aliasAddress: card.aliasAddress,
    balance: card.balance,
    status: card.status,
  };
}

export type CardLookupStatus =
  | "idle" // no lookup is running (disabled by the caller, or no auth yet)
  | "loading" // a request is in flight for the current card number
  | "found" // the chain returned a matching card
  | "not-found" // the chain answered, definitively, with no such card
  | "error"; // the lookup could not be completed (network/server error)

export interface UseCardLookupResult {
  status: CardLookupStatus;
  card: CardRecord | null;
  /** Re-run the lookup — used to recover from the `error` state. */
  refetch: () => void;
}

/**
 * Looks a card number up on-chain — the fallback used when it isn't among the
 * recent cards already loaded. Gated by `enabled` so the request only fires
 * when the caller has decided a lookup is warranted (e.g. the local list came
 * up empty for a full PAN).
 *
 * Crucially, a request that fails (network/5xx) surfaces as `error`, never as
 * `not-found` — only a successful response with no card is a true miss, so the
 * UI never tells the operator a card doesn't exist when the lookup simply
 * couldn't reach the chain.
 */
export function useCardLookup(
  cardNumber: string,
  enabled: boolean,
): UseCardLookupResult {
  const { user } = useAuth();
  const networkType = useSelector(networkTypeSelector);
  const { param: token } = useSettlementToken();
  const normalized = cardNumber.trim();
  const active = !!user && enabled && normalized.length > 0;

  const query = useQuery<SpecificCard>({
    // The looked-up card carries a BALANCE, so the result is token-scoped.
    queryKey: ["card-lookup", normalized, token],
    queryFn: () => card_services.lookupCard(normalized, networkType, token),
    enabled: active,
    retry: false,
    staleTime: 60_000,
  });

  // A miss answers 200 with `{found:false}` and NO cardNumber, so its absence is the miss signal.
  // Keep it that way: the backend must never echo the searched PAN back on a miss.
  const card =
    active && query.data?.cardNumber ? toCardRecord(query.data) : null;

  let status: CardLookupStatus;
  if (!active) {
    status = "idle";
  } else if (query.isFetching) {
    status = "loading";
  } else if (query.isError) {
    status = "error";
  } else if (query.isSuccess) {
    status = card ? "found" : "not-found";
  } else {
    // Active but not yet fetching — the tick between enabling and the request
    // actually starting. Treat as loading so we never flash a stale outcome.
    status = "loading";
  }

  return { status, card, refetch: query.refetch };
}
